/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (CURRENT) - Initial creation of the IndexedDB utility for whitelabel configuration.
 *
 * -----
 * SECURITY_RISK: LOW - This module provides access to IndexedDB for storing UI whitelabel configuration.
 *                      It does not store sensitive user data or API keys directly.
 * INTENT: Centralize and standardize IndexedDB interactions for whitelabel configuration,
 *         ensuring consistent data access and persistence across the UI.
 * PROMPTER_INSTRUCTIONS: Ensure all IndexedDB operations are robust, handle errors gracefully,
 *                        and are aligned with the application's data persistence strategy.
 * LLM_CONFIDENCE: 100% - Standard IndexedDB utility implementation.
 */
import { WhitelabelConfig } from '../types/whitelabel.d.ts';

const DB_NAME = 'whitelabelConfigDB';
const STORE_NAME = 'whitelabelSettings';
const WHITELABEL_CONFIG_KEY = 'whitelabelConfig'; // The key under which the whitelabel config is stored

/**
 * Opens the IndexedDB database for whitelabel configuration and creates the object store if it doesn't exist.
 * @returns A Promise that resolves with the IDBDatabase object.
 */
export const openWhitelabelDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      console.warn("IndexedDB not available in this environment for whitelabel config.");
      reject(new Error("IndexedDB not available"));
      return;
    }

    const request = indexedDB.open(DB_NAME, 1);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = (event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };

    request.onerror = (event) => {
      console.error("Error opening whitelabel IndexedDB:", (event.target as IDBOpenDBRequest).error);
      reject((event.target as IDBOpenDBRequest).error);
    };
  });
};

/**
 * Retrieves the whitelabel configuration stored under the WHITELABEL_CONFIG_KEY.
 * @returns A Promise that resolves with the WhitelabelConfig object, or an empty object if not found.
 */
export const getWhitelabelConfig = async (): Promise<WhitelabelConfig> => {
  try {
    const db = await openWhitelabelDB();
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(WHITELABEL_CONFIG_KEY);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        resolve(request.result || {});
      };
      request.onerror = (event) => {
        console.error("Error getting whitelabel config from IndexedDB:", (event.target as IDBRequest).error);
        reject((event.target as IDBRequest).error);
      };
    });
  } catch (error) {
    console.error("Error accessing IndexedDB for whitelabel config:", error);
    return {}; // Return empty object on error
  }
};

/**
 * Saves a partial or full whitelabel configuration object to IndexedDB under the WHITELABEL_CONFIG_KEY.
 * Merges with existing configuration.
 * @param newConfig A partial object containing whitelabel settings to save.
 * @returns A Promise that resolves when the configuration is saved.
 */
export const saveWhitelabelConfig = async (newConfig: Partial<WhitelabelConfig>): Promise<void> => {
  try {
    const existingConfig = await getWhitelabelConfig(); // Get current config
    const updatedConfig = { ...existingConfig, ...newConfig };

    const db = await openWhitelabelDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    store.put(updatedConfig, WHITELABEL_CONFIG_KEY);

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = (event) => {
        console.error("Error saving whitelabel config to IndexedDB:", (event.target as IDBTransaction).error);
        reject((event.target as IDBTransaction).error);
      };
    });
  } catch (error) {
    console.error("Error accessing IndexedDB for saving whitelabel config:", error);
    throw error;
  }
};
